import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import SignaturePadLib from 'signature_pad';
import { Button, Box } from '@mui/material';

interface CustomSignaturePadProps {
    value: string;
    onChange: (dataUrl: string) => void;
    disabled?: boolean;
    width?: number;
    height?: number;
}

const CustomSignaturePad: React.FC<CustomSignaturePadProps> = ({
    value,
    onChange,
    disabled = false,
    width = 400,
    height = 200,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    // #261: shrink the canvas to the available width on narrow screens. Measured when the editable
    // canvas mounts (it may start read-only) — resizing a canvas clears it, so no live resize.
    const [canvasWidth, setCanvasWidth] = useState(width);

    useLayoutEffect(() => {
        if (disabled) return;
        const available = wrapperRef.current?.clientWidth ?? 0;
        setCanvasWidth(available > 0 ? Math.min(width, available) : width);
    }, [width, disabled]);
    const signaturePadRef = useRef<SignaturePadLib | null>(null);

    const valueRef = useRef(value);
    valueRef.current = value;

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || disabled) return;

        const pad = new SignaturePadLib(canvas, {
            penColor: '#000',
            backgroundColor: 'rgba(255, 255, 255, 0)',
        });

        pad.addEventListener('endStroke', () => {
            onChangeRef.current(pad.toDataURL('image/png'));
        });

        signaturePadRef.current = pad;

        if (valueRef.current) {
            pad.fromDataURL(valueRef.current, { width: canvasWidth, height: Math.round((height * canvasWidth) / width) });
        }

        return () => {
            pad.off();
            signaturePadRef.current = null;
        };
    }, [disabled, width, height, canvasWidth]);

    const handleClear = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
            onChange('');
        }
    };

    // Read-only mode: show signature as image
    if (disabled) {
        return (
            <Box>
                {value ? (
                    <img
                        src={value}
                        alt="חתימה"
                        style={{
                            width,
                            maxWidth: '100%',
                            height,
                            border: '1px solid #d3d3d3',
                            borderRadius: '8px',
                            backgroundColor: '#fafafa',
                            objectFit: 'contain',
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            width,
                            maxWidth: '100%',
                            height,
                            border: '1px solid #d3d3d3',
                            borderRadius: '8px',
                            backgroundColor: '#fafafa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#999',
                            fontSize: '14px',
                        }}
                    >
                        לא נחתם
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box ref={wrapperRef}>
            <canvas
                ref={canvasRef}
                width={canvasWidth}
                height={height}
                style={{
                    border: '1px solid #d3d3d3',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa',
                    cursor: 'crosshair',
                    touchAction: 'none',
                    display: 'block',
                    direction: 'ltr',
                }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-start' }}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={handleClear}
                    sx={{
                        color: '#2099B4',
                        borderColor: '#2099B4',
                        borderRadius: '20px',
                        fontSize: '12px',
                        '&:hover': {
                            borderColor: '#1a7a91',
                            backgroundColor: 'rgba(32, 153, 180, 0.04)',
                        },
                    }}
                >
                    נקה חתימה
                </Button>
            </Box>
        </Box>
    );
};

export default CustomSignaturePad;
